import {
  type CivilDate,
  compareCivilDate,
  maxCivilDate,
  todayCivilDate,
} from '~/models/civilDate'

/** Default a newly added plan-master row to max(JST today, record validFrom). */
export function planMasterRowDefaultValidFrom(
  recordValidFrom: CivilDate | undefined,
  today: CivilDate = todayCivilDate(),
): CivilDate {
  return recordValidFrom === undefined
    ? today
    : maxCivilDate(today, recordValidFrom)
}

/**
 * FK select の候補を絞る対象日 (AC-MOI-023)。行の適用開始日、レコードの適用開始日、
 * 当日の順に、最初に入力されているものを使う。
 */
export function resolveAsOfDate(
  rowValidFrom: CivilDate | null | undefined,
  recordValidFrom: CivilDate | null | undefined,
  today: CivilDate,
): CivilDate {
  return rowValidFrom ?? recordValidFrom ?? today
}

/**
 * 適用期間 (validFrom / validTo) から導出される 3 値ステータス。
 * 一覧のステータス列・失効バッジ・適用前バッジの表示元 (AC-06/07/08)。
 */
export type ExpiryStatus = 'active' | 'future' | 'expired'

function padTwoDigits(n: number): string {
  return n.toString().padStart(2, '0')
}

/**
 * 一覧の適用開始日 / 適用終了日列で使う表示用フォーマット (YYYY/MM/DD)。
 * 7 タブ共通 (AC-06、3 列追加のうちの 2 列)。
 */
export function formatCivilDate(date: CivilDate): string {
  return `${date.year}/${padTwoDigits(date.month)}/${padTwoDigits(date.day)}`
}

/**
 * 適用期間の 3 値ステータスを純粋関数で判定する (occto-master-valid-period-6tabs
 * 設計書 §5.4)。
 *
 * - validTo が今日より前 (= validTo < today) → 'expired'（validTo === today は 'active'。
 *   同日は有効日として扱う）
 * - validFrom が今日より後 (= validFrom > today) → 'future'（適用開始前）
 * - それ以外 → 'active'
 *
 * TODO(#3795): 一覧で「これから有効になるデータ」を区別表示してよいか
 * (= 'future' ステータスの表示自体が spec 未定義で mock-app 準拠。 PdM 確認待ち。
 * 否認時は ExpiredBadge / AC-08 / i18n key / ステータス列が手戻る)。
 */
/**
 * 候補外 (as-of narrow 後の候補に無い) 既存参照の fallback 表示ラベルを解決する
 * (occto-master-fk-as-of-filter 設計書 Decision 2/4、AC-MOI-023)。
 *
 * 4 値分類:
 * - `unfiltered` (絞込前の全件一覧) に `refId` が存在しない → 物理削除
 *   (`opts.deletedLabel`)
 * - 存在し、 `targetDate` が `validTo` を超過 (包含境界、 `isActiveOn` と同一規約)
 *   → 適用終了 (`{label}${opts.expiredSuffix}`)
 * - `opts.future` を指定し、 `targetDate` が適用開始日より前 (`expiryStatus` の
 *   'future') → 適用前 (`{label}${opts.future.suffix}`)
 * - それ以外 (active だが他条件で候補外 / `opts.future` 未指定の未来 /
 *   targetDate 未確定) → 実名のみ (`{label}`)
 *
 * `refId` が空文字の場合は `undefined` (= 未選択、呼び出し側は placeholder 表示に戻す)。
 * `targetDate` が undefined (transient) の間は「終了」判定を skip し実名を返す
 * (as-of フィルタ自体の fail-open と整合させる)。
 *
 * `unfiltered` が `undefined` の場合も `undefined` を返す (= placeholder 表示)。
 * これは list クエリが未取得の一時状態を表すセンチネルで、 `[]` (クエリ取得済み
 * だが 0 件) とは区別する。 未取得の間 callsite が `[]` を渡すと、 実在する
 * 参照が取得完了までの一瞬「(削除済み)」に誤表示される (PR #5920 レビュー
 * 指摘)。 `[]` (取得済みで空) は従来どおり 3 値分類 (不在 → `deletedLabel`)
 * へ進む。
 */
export function resolveExpiryFallbackLabel<T>(
  unfiltered: readonly T[] | undefined,
  refId: string,
  targetDate: CivilDate | undefined,
  opts: {
    idOf: (item: T) => string
    labelOf: (item: T) => string
    validTo: (item: T) => CivilDate | undefined
    deletedLabel: string
    expiredSuffix: string
    /**
     * 指定した時だけ、 対象日が適用開始日より前の参照に `suffix` を付ける
     * (AC-MOI-023「{名称}（適用前）」)。 未指定の呼び出し側は従来どおり終了だけを判定する。
     */
    future?: { validFrom: (item: T) => CivilDate; suffix: string }
  },
): string | undefined {
  if (refId === '') return undefined
  if (unfiltered === undefined) return undefined
  const found = unfiltered.find((item) => opts.idOf(item) === refId)
  if (found === undefined) return opts.deletedLabel
  const label = opts.labelOf(found)
  if (targetDate === undefined) return label
  const validTo = opts.validTo(found)
  if (isValidToPast(validTo, targetDate)) return `${label}${opts.expiredSuffix}`
  if (
    opts.future !== undefined &&
    expiryStatus(opts.future.validFrom(found), validTo, targetDate) === 'future'
  ) {
    return `${label}${opts.future.suffix}`
  }
  return label
}

/**
 * 適用終了日が「今日」より前 (= 過去日) かを判定する。
 *
 * 境界規約は `expiryStatus` の 'expired' 判定と同一 (validTo === today は
 * 有効日として扱い false)。 適用開始日を持たない文脈 (編集モーダルの
 * 適用終了日欄そのもの) からも参照できるよう、 validTo 単独の述語として
 * 切り出す。 型述語にしてあるのは、 呼び出し側が「過去日だと分かった後」に
 * 日付を文言へ埋め込むため (`ExpiredMasterAlert`)。
 */
export function isValidToPast(
  validTo: CivilDate | undefined,
  today: CivilDate,
): validTo is CivilDate {
  return validTo !== undefined && compareCivilDate(validTo, today) < 0
}

export function expiryStatus(
  validFrom: CivilDate,
  validTo: CivilDate | undefined,
  today: CivilDate,
): ExpiryStatus {
  if (isValidToPast(validTo, today)) {
    return 'expired'
  }
  if (compareCivilDate(validFrom, today) > 0) {
    return 'future'
  }
  return 'active'
}
